/*
 * Metrics Explorer — ad-hoc time-series query against the platform's
 * job history. Backed by /api/observability/metrics/query. Three
 * metrics for the MVP: completed-job count, failed-job count, p95
 * latency. Each rendered as a sparkbar.
 */

import { useEffect, useMemo, useState } from "react";
import { api, ApiError } from "../lib/api.js";
import PageBreadcrumbs from "../layouts/PageBreadcrumbs.js";
import { SkeletonStat } from "../components/Skeleton.js";

const METRICS: Array<{ key: string; label: string; unit: string }> = [
  { key: "jobs.completed",        label: "Completed jobs",     unit: "count" },
  { key: "jobs.failed",           label: "Failed jobs",        unit: "count" },
  { key: "jobs.duration_ms_p95",  label: "Job latency (p95)",  unit: "ms" }
];
const BUCKETS: Array<{ key: "hour" | "minute"; label: string; defaultWindowHours: number }> = [
  { key: "hour",   label: "Hourly (24h window)", defaultWindowHours: 24 },
  { key: "minute", label: "Minute (1h window)",  defaultWindowHours: 1 }
];

type Series = Awaited<ReturnType<typeof api.observabilityMetricsQuery>>;

function fmtVal(v: number, unit: string): string {
  if (unit === "ms") {
    if (v < 1000) return `${Math.round(v)} ms`;
    if (v < 60_000) return `${(v / 1000).toFixed(1)} s`;
    return `${Math.round(v / 60_000)} m`;
  }
  return Math.round(v).toLocaleString();
}

export default function MetricsExplorer() {
  const [metric, setMetric] = useState<string>("jobs.completed");
  const [bucket, setBucket] = useState<"hour" | "minute">("hour");
  const [jobType, setJobType] = useState<string>("");
  const [series, setSeries] = useState<Series | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setSeries(null);
    api.observabilityMetricsQuery({ metric, bucket, jobType: jobType || undefined })
      .then(setSeries)
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)));
  }, [metric, bucket, jobType]);

  const meta = useMemo(() => METRICS.find((m) => m.key === metric)!, [metric]);
  const max = useMemo(() => series ? Math.max(1, ...series.points.map((p) => p.value)) : 1, [series]);

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>Metrics explorer</h1>
          <p className="muted">Ad-hoc query over runtime metrics. Pick a metric + bucket; results plot live from the async-job history.</p>
        </div>
      </header>

      {error && <div className="ts-alert ts-alert-error">{error}</div>}

      <section className="transfer-filters">
        <label className="transfer-filter">
          Metric
          <select value={metric} onChange={(e) => setMetric(e.target.value)}>
            {METRICS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
          </select>
        </label>
        <label className="transfer-filter">
          Bucket
          <select value={bucket} onChange={(e) => setBucket(e.target.value as "hour" | "minute")}>
            {BUCKETS.map((b) => <option key={b.key} value={b.key}>{b.label}</option>)}
          </select>
        </label>
        <label className="transfer-filter transfer-filter-grow">
          Filter by job type (optional)
          <input type="text" value={jobType} onChange={(e) => setJobType(e.target.value)} placeholder="e.g. tracker-events-sync" />
        </label>
      </section>

      <section className="stat-grid">
        {!series ? (<><SkeletonStat /><SkeletonStat /><SkeletonStat /></>) : (
          <>
            <div className="stat"><div className="stat-label">Total samples</div><div className="stat-value">{series.summary.totalSamples}</div><div className="stat-hint">in window</div></div>
            <div className="stat"><div className="stat-label">Peak {meta.unit}</div><div className="stat-value">{fmtVal(series.summary.max, meta.unit)}</div></div>
            <div className="stat"><div className="stat-label">Sum / count</div><div className="stat-value">{fmtVal(series.summary.sum, meta.unit)}</div></div>
          </>
        )}
      </section>

      <section className="section">
        <div className="section-head"><h2>{meta.label} — {bucket} buckets</h2></div>
        {!series && <div className="loading">Loading series…</div>}
        {series && series.points.length === 0 && (
          <div className="empty-state-card">
            <h2 style={{ marginTop: 0 }}>No data in this window</h2>
            <p>Trigger jobs of the requested type, or widen the bucket / clear the filter.</p>
          </div>
        )}
        {series && series.points.length > 0 && (
          <>
            <div className="warehouse-series" style={{ height: 180 }}>
              {series.points.map((p) => (
                <div key={p.ts} className="warehouse-series-bar" title={`${new Date(p.ts).toLocaleString()}: ${fmtVal(p.value, meta.unit)}`}>
                  <div className="warehouse-series-fill" style={{ height: `${(p.value / max) * 100}%` }} />
                </div>
              ))}
            </div>
            <details style={{ marginTop: 12 }}>
              <summary className="muted" style={{ cursor: "pointer" }}>Raw points ({series.points.length})</summary>
              <pre style={{ fontSize: 11, maxHeight: 240, overflow: "auto", padding: 8, background: "var(--surface-muted, #f9fafb)", borderRadius: 4 }}>
                {JSON.stringify(series.points, null, 2)}
              </pre>
            </details>
          </>
        )}
      </section>
    </div>
  );
}
